import { DateText } from '@batiflow/ui';
import { __css, mergeClasses, tokens } from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';
import { DecisionChip } from './approval-badges';
import { reviewerState, type ApprovalFlow, type ApprovalStep } from './approvals-model';

const useStyles = __css({"list":{"dclx09":"ftrb29c","Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","B1hrpyx":"f1jat206","mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1wn3y0l"},"step":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f778t4r","Dbcxam":0,"rjzwhg":0,"Bblux5w":"f9b8bpc","Bnt446c":0,"B1hrpyx":0,"Fjva8n":"f12n3xlw","Beyfa6y":0,"Bbmb7ep":0,"Btl43ni":0,"B7oj6ja":0,"Dimara":"ft85np5","De3pzq":"f1dmdbja","B4m11q7":"fnkaqeo","rm1204":"fwyddsq","ijz38e":"f1ftuoky"},"active":{"De3pzq":"f16xkysk","ijz38e":"fyvlgxw"},"head":{"mc9l5x":"f22iagw","Eh141a":"fni485r","Bt984gj":"f122n59","i8kkvl":"fsnqrgy","Belr9w4":"f1j0q4x9"},"title":{"Bhrd7zp":"fl43uef"},"meta":{"sj55zd":"fkfq4zb","Be2twd7":"fy9rknc"},"current":{"Be2twd7":"fy9rknc","Bhrd7zp":"fl43uef","sj55zd":"f16muhyy"},"reviewers":{"dclx09":"ftrb29c","Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","B1hrpyx":"f1jat206","mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f778t4r"},"reviewer":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1j0q4x9"},"reviewerLine":{"mc9l5x":"f22iagw","Eh141a":"fni485r","Bt984gj":"f122n59","i8kkvl":"fsnqrgy","Belr9w4":"f1j0q4x9"},"name":{"Btd35i7":"fjksvth"},"comment":{"Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","sj55zd":"f19n0e5","Be2twd7":"fy9rknc","Btd35i7":"fjksvth","Huce71":"fibxuh5"}});

interface StepRowProps {
  step: ApprovalStep;
  currentUserId: string | undefined;
}

function StepRow({ step, currentUserId }: StepRowProps) {
  const { t } = useTranslation('documents');
  const styles = useStyles();
  const active = step.status === 'active';

  return (
    <li
      className={mergeClasses(styles.step, active && styles.active)}
      aria-current={active ? 'step' : undefined}
    >
      <div className={styles.head}>
        <span className={styles.title}>{t('approvals.stepper.step', { n: step.ordinal })}</span>
        <span className={styles.meta}>{t(`approvals.stepper.mode.${step.mode}`)}</span>
        {active ? <span className={styles.current}>{t('approvals.stepper.current')}</span> : null}
        {step.status === 'waiting' || step.status === 'cancelled' ? (
          <span className={styles.meta}>{t(`approvals.stepper.stepStatus.${step.status}`)}</span>
        ) : null}
        {step.outcome && step.reviewers.length > 1 ? (
          <DecisionChip decision={step.outcome} />
        ) : null}
        {step.dueOn ? (
          <span className={styles.meta}>
            {t('approvals.stepper.due')} <DateText value={step.dueOn} />
          </span>
        ) : null}
      </div>
      <ul
        className={styles.reviewers}
        aria-label={t('approvals.stepper.reviewers', { n: step.ordinal })}
      >
        {step.reviewers.map((reviewer) => {
          const decision = step.decisions.find((item) => item.reviewerId === reviewer.id);
          const state = reviewerState(step, reviewer.id);
          return (
            <li key={reviewer.id} className={styles.reviewer}>
              <span className={styles.reviewerLine}>
                <bdi className={styles.name}>
                  {reviewer.name ?? t('approvals.stepper.unknownReviewer')}
                </bdi>
                {reviewer.id === currentUserId ? (
                  <span className={styles.meta}>{t('approvals.stepper.you')}</span>
                ) : null}
                {decision ? (
                  <>
                    <DecisionChip decision={decision.decision} />
                    <span className={styles.meta}>
                      <DateText value={decision.decidedAt} variant="dateTime" />
                    </span>
                  </>
                ) : state === 'turn' ? (
                  <span className={styles.meta}>{t('approvals.stepper.turn')}</span>
                ) : state === 'waiting' ? (
                  <span className={styles.meta}>{t('approvals.stepper.waiting')}</span>
                ) : state === 'later' ? (
                  <span className={styles.meta}>{t('approvals.stepper.later')}</span>
                ) : null}
              </span>
              {decision?.comment ? <p className={styles.comment}>{decision.comment}</p> : null}
            </li>
          );
        })}
      </ul>
    </li>
  );
}

export interface ApprovalStepperProps {
  flow: Pick<ApprovalFlow, 'steps'>;
  /** The signed-in person: their line says "(you)". */
  currentUserId?: string | undefined;
}

/**
 * The steps of a flow in order: mode, due date, reviewers with their decision (VSO, VAO or REF as
 * an icon and a text, with the observation) or who is awaited. The active step is highlighted
 * and says so in words.
 */
export function ApprovalStepper({ flow, currentUserId }: ApprovalStepperProps) {
  const { t } = useTranslation('documents');
  const styles = useStyles();
  return (
    <ol className={styles.list} aria-label={t('approvals.stepper.label')}>
      {flow.steps.map((step) => (
        <StepRow key={step.id} step={step} currentUserId={currentUserId} />
      ))}
    </ol>
  );
}


import "/opt/batiflow/app/apps/web/src/features/documents/approvals/approval-stepper.griffel.css";